
import moment from 'moment'
import { KIND_COLORS, KIND_ICONS, KIND_ORDER, SOURCE_LABELS, isAepDay, presentDay, trackSpan, placeDots } from '~/util/client-changes'

const TAB_LABELS = {
    '': 'Profile',
    demographics: 'Demographics',
    'enrollment-period': 'Enrollment Period',
    'income-surcharges': 'Income Surcharges',
    'health-lifestyle': 'Health/Lifestyle',
    providers: 'Doctors & Hospitals',
    drugs: 'Drugs',
    pharmacies: 'Pharmacies',
}

export default {
    name: 'ClientChangesTimeline',
    props: {
        uid: { type: Number, required: true },
    },
    data () {
        return {
            payload: null,
            selected: [],
            width: 1000,
            colors: KIND_COLORS,
            icons: KIND_ICONS,
        }
    },
    computed: {
        days () {
            return (this.payload && this.payload.days) || []
        },
        presented () {
            return this.days.map(presentDay)
        },
        latest () {
            return this.presented[0]
        },
        span () {
            return trackSpan(this.days, this.payload.today)
        },
        dots () {
            return placeDots(this.days, this.span, this.width)
        },
        summary () {
            if (this.days.length === 1) {
                return `· ${this.short(this.latest.day)} (${this.sources(this.latest)}):`
            }
            const oldest = this.days[this.days.length - 1].day
            const unread = this.presented.filter(d => !d.read).length
            const parts = [`${this.days.length} days since ${this.short(oldest)}`]
            if (unread) parts.push(`${unread} not opened`)
            parts.push(`latest ${this.short(this.latest.day)}:`)
            return `· ${parts.join(' · ')}`
        },
        legendKinds () {
            const kinds = KIND_ORDER.filter(k => this.presented.some(d => d.kind === k))
            return this.presented.some(d => d.read) ? [...kinds, 'read'] : kinds
        },
        selectedDays () {
            return this.presented.filter(d => this.selected.includes(d.day))
        },
    },
    watch: {
        uid () {
            this.selected = []
            this.fetch()
        },
    },
    mounted () {
        this.fetch()
        document.addEventListener('keydown', this.onKeydown)
    },
    beforeDestroy () {
        document.removeEventListener('keydown', this.onKeydown)
        if (this.resizeObserver) this.resizeObserver.disconnect()
    },
    methods: {
        async fetch () {
            try {
                this.payload = await this.$api.getClientChanges(this.uid)
            } catch (e) {
                // The bar is a convenience: a failure hides it, never the page.
                console.warn('client changes: could not load', e)
                this.payload = null
            }
            this.$nextTick(this.observeWidth)
        },
        observeWidth () {
            const track = this.$refs.track
            if (!track || this.resizeObserver) return
            if (typeof ResizeObserver === 'undefined') {
                this.width = track.clientWidth || 1000
                return
            }
            this.resizeObserver = new ResizeObserver(([entry]) => {
                this.width = entry.contentRect.width || 1000
            })
            this.resizeObserver.observe(track)
        },
        async openDays (days) {
            this.selected = days
            try {
                this.payload = await this.$api.openClientChanges(this.uid, days)
            } catch (e) {
                console.warn('client changes: could not mark opened', e)
            }
        },
        openDot (dot) {
            // The open dot toggles its panel shut
            if (this.isSelected(dot)) {
                this.selected = []
                return
            }
            return this.openDays(dot.days.map(d => d.day))
        },
        openLatest () {
            return this.openDays([this.latest.day])
        },
        async unread (day) {
            try {
                this.payload = await this.$api.unreadClientChanges(this.uid, day)
            } catch (e) {
                console.warn('client changes: could not mark unread', e)
            }
        },
        isSelected (dot) {
            return dot.days.some(d => this.selected.includes(d.day))
        },
        dotStyle (dot) {
            return {
                left: `${dot.pos * 100}%`,
                width: `${dot.size}px`,
                height: `${dot.size}px`,
                background: dot.read ? KIND_COLORS.read : KIND_COLORS[dot.kind],
            }
        },
        dotTitle (dot) {
            const first = dot.days[0]
            const last = dot.days[dot.days.length - 1]
            const when = dot.days.length > 1 ? `${this.short(first.day)} – ${this.short(last.day)}` : this.short(first.day)
            return dot.read ? `${when} · opened by ${last.read.by_name}` : when
        },
        dayDate (d) {
            return moment.utc(d.day, 'YYYY-MM-DD').format('ddd, MMM D')
        },
        isAep (d) {
            return isAepDay(d)
        },
        // The day's other sources, as text after the AEP badge
        otherSources (d) {
            return d.sources.filter(s => s !== 'aep_survey').map(s => SOURCE_LABELS[s] || s).join(', ')
        },
        sources (d) {
            return d.sources.map(s => SOURCE_LABELS[s] || s).join(', ')
        },
        short (day) {
            return moment.utc(day, 'YYYY-MM-DD').format('MMM D')
        },
        openedAt (read) {
            return moment(read.at).format('MMM D h:mm a')
        },
        tabPath (line) {
            return `/survey/${this.uid}/${line.tab}`
        },
        tabLabel (line) {
            return `${TAB_LABELS[line.tab] || 'Profile'} tab`
        },
        onKeydown (e) {
            if (e.key === 'Escape') this.selected = []
        },
    },
}
